<template>
  <div>
    <div :class="containerClass" class="d-flex justify-content-between mt-3">
      <h2>
        View Courses
        <i class="material-icons icon-16pt clickable-item" v-b-popover.hover.top="`View courses of each program.`"
          >info</i
        >
      </h2>
      <div>
        <b-btn size="md" variant="primary" :to="{ name: 'sa-create-course' }">
          Add Course
        </b-btn>
      </div>
    </div>

    <div class="page-section">
      <div :class="containerClass">
        <div class="card mb-0">
          <div class="card-header">
            <div class="row align-items-center" style="white-space: nowrap">
              <div class="col-lg-auto">
                <form class="search-form search-form--light d-lg-inline-flex mb-8pt mb-lg-0" @submit.prevent="">
                  <b-form-input class="w-lg-auto" placeholder="Search Courses" v-model="searchTerm" @input="onSearch" />
                  <b-btn variant="flush" type="submit">
                    <md-icon>search</md-icon>
                  </b-btn>
                </form>
              </div>

              <div v-if="!isTabSmallScreen" class="col-lg d-flex flex-wrap justify-content-end">
                <div class="row mx-auto mb-2  w-100 mt-2">
                  <div class="col-md-6">
                    <v-select
                      id="prog_category"
                      class="form-control v-select-custom"
                      label="title"
                      v-model="program_category"
                      :reduce="pg => pg.id"
                      placeholder="Select Program Category"
                      :options="allPrograms"
                      :loading="areProgramsLoading"
                      @input="onProgramChange"
                    >
                    </v-select>
                  </div>
                </div>
              </div>
              <div v-if="isTabSmallScreen" class="col-lg d-flex flex-wrap justify-content-end">
                <div class="mb-2 mt-2" style="width: 100%;">
                  <div class="mb-2">
                    <v-select
                      id="prog_category"
                      class="form-control v-select-custom"
                      label="title"
                      v-model="program_category"
                      :reduce="pg => pg.id"
                      placeholder="Select Program Category"
                      :options="allPrograms"
                      :loading="areProgramsLoading"
                      @input="onProgramChange"
                    >
                    </v-select>
                  </div>
                </div>
              </div>
            </div>

            <b-table
              :fields="tableColumns"
              :items="courses"
              v-if="courses.length"
              :busy="isLoading"
              head-variant="light"
              class="table-nowrap"
              hover
              responsive
              no-local-sorting
              @sort-changed="onSortChange"
              @row-clicked="rowClicked"
            >
              <template #table-busy>
                <div class="text-center my-2">
                  <b-spinner class="align-middle mr-2"></b-spinner>
                  <strong>Loading...</strong>
                </div>
              </template>

              <template #cell(image_url)="data">
                <div class="text-left">
                  <user-avatar slot="aside" size="md" :fileUrl="data.item.image_url"> </user-avatar>
                </div>
              </template>

              <template #cell(title)="data"
                ><strong> {{ data.item.title }}</strong></template
              >

              <template #cell(custom_tags)="data">
                <div class="text-left" v-if="data.item.custom_tags && data.item.custom_tags.length">
                  <b-badge
                    v-for="(tag, idx) in data.item.custom_tags"
                    :key="idx"
                    variant="info"
                    class="mr-1 mb-1"
                  >
                    {{ tag }}
                  </b-badge>
                </div>
                <span v-else class="text-muted">—</span>
              </template>

              <template #cell(program_category)="data">
                <div class="text-left">{{ data.item.program_category.title }}</div>
              </template>
              <template #cell(is_published)="data">
                <md-icon v-if="data.item.is_published" class="text-success">done</md-icon>
                <md-icon v-else class="text-danger">close</md-icon>
                <!-- <div class="text-center">{{ data.item.is_published }}</div> -->
              </template>

              <template #head(actions)="">
                <span></span>
              </template>
              <template #cell(actions)="data">
                <b-dropdown :boundary="'main'" no-flip variant="flush" toggle-class="text-muted" no-caret @click.stop>
                  <template #button-content>
                    <i class="material-icons icon-24pt text-info">more_vert</i>
                  </template>
                  <b-dropdown-item @click.stop="openDuplicateModal(data.item)">
                    <md-icon class="text-info mr-2">content_copy</md-icon>
                    Duplicate
                  </b-dropdown-item>
                  <b-dropdown-divider></b-dropdown-divider>
                  <b-dropdown-item
                    @click.stop
                    :to="{
                      name: 'sa-edit-lms-course',
                      params: { id: data.item.id },
                    }"
                  >
                    <md-icon class="text-info mr-2">edit</md-icon>
                    {{ $t('generalMsgs.edit') }}
                  </b-dropdown-item>
                  <b-dropdown-divider></b-dropdown-divider>
                  <b-dropdown-item @click.stop="confirmDeleteCourse(data.item.id)">
                    <md-icon class="text-danger mr-2">delete</md-icon>
                    {{ $t('generalMsgs.remove') }}
                  </b-dropdown-item>
                </b-dropdown>
              </template>
            </b-table>
            <div class="card-footer">
              <pagination
                v-model="currentPage"
                :total-rows="totalCourses"
                :per-page="perPage"
                @change="onPageChange"
                aria-controls="courses-table"
              />
            </div>
          </div>
          <duplicate-course-modal
            v-model="showDuplicateModal"
            :initial-title="duplicateForm.title"
            :initial-program-category="duplicateForm.program_category"
            :all-programs="allPrograms"
            :are-programs-loading="areProgramsLoading"
            :is-loading="isDuplicateLoading"
            @hidden="resetDuplicateModal"
            @cancel="resetDuplicateModal"
            @confirm="confirmDuplicateCourse"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce, get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import Page from '@/components/Page.vue';
import { DEFAULT_PAGE_SIZE } from '../../../common/constants';
import UserAvatar from '../../../components/User/UserAvatar.vue';
import { formatFullDate } from '@/common/utils';
import Pagination from '../../../components/Ui/Pagination.vue';
import DuplicateCourseModal from './DuplicateCourseModal.vue';
import persistListFilters from '@/mixins/persist-list-filters';

export default {
  components: {
    UserAvatar,
    Pagination,
    DuplicateCourseModal,
  },
  extends: Page,
  mixins: [persistListFilters],
  filterKeys: ['searchTerm', 'program_category', 'currentPage', 'offset'],

  data() {
    return {
      title: 'View Courses',
      date: null,
      isLoading: true,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalCourses: 0,
      searchTerm: null,
      program: null,
      allPrograms: [],
      program_category: '',
      areProgramsLoading: false,
      windowWidth: window.innerWidth,
      courses: [],
      limit: 15,
      offset: 0,
      search: '',
      showDuplicateModal: false,
      duplicateForm: {
        title: '',
        program_category: null,
      },
      selectedCourseId: null,
      isDuplicateLoading: false,
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    tableColumns() {
      return [
        {
          key: 'actions',
          label: '',
          class: 'text-left',
          thStyle: { minWidth: '120px' },
        },
        { key: 'image_url', label: '' },
        { key: 'title', label: 'Title', class: 'text-left' },
        { key: 'custom_tags', label: 'Tags', class: 'text-left' },
        { key: 'program_category', label: 'Program', class: 'text-left' },
        { key: 'is_published', label: 'Published', class: 'text-left' },
        {
          key: 'created_at',
          label: 'Created At',
          tdClass: 'clickable-item text-left',
          formatter: value => (value ? formatFullDate(value) : value),
        },
      ];
    },

    isTabSmallScreen() {
      return this.windowWidth <= 767;
    },
  },

  methods: {
    get,
    ...mapActions('courses', ['getLmsCourses', 'deleteCourse', 'duplicateCourse']),
    ...mapActions('program', ['getAllPrograms']),

    clearFilters() {
      this.program = null;
      this.tag_id = null;
    },
    dateChange(value) {
      if (value[0]) {
        this.date = value;
      } else {
        this.date = null;
      }
    },
    onPageChange(pageNum) {
      this.offset = (pageNum - 1) * this.perPage;
      this.fetchAllCourses();
    },
    async fetchPrograms() {
      this.areProgramsLoading = true;
      try {
        const res = await this.getAllPrograms();
        this.allPrograms = res.data;
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.areProgramsLoading = false;
    },
    async confirmDeleteCourse(id) {
      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm('Are you sure you want to delete the Course?', {
          title: 'Delete Course',
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'success',
          cancelVariant: 'danger',
          okTitle: 'Yes',
          cancelTitle: 'No',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        });
        if (isConfirmed) {
          await this.onDeleteCourse(id);
          this.fetchAllCourses();
          // const refreshPage = this.careers.length > 1 ? this.currentPage : this.currentPage - 1;
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
    async onDeleteCourse(id) {
      try {
        await this.deleteCourse(id);
        this.makeToast({ variant: 'success', msg: 'Course Successfully Deleted' });
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
    async fetchAllCourses(params = {}) {
      this.isLoading = true;
      try {
        const res = await this.getLmsCourses({
          limit: this.perPage,
          offset: this.offset,
          ...(this.searchTerm && { search: this.searchTerm }),
          ...(this.program_category ? { program_category__id: this.program_category } : {}),
          pagination: true,
          ...params,
        });

        this.courses = res.data.results;
        this.totalCourses = res.data.count;
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    rowClicked(row) {
      this.$router.push({
        name: 'sa-edit-lms-course',
        params: { id: row.id },
      });
    },

    onSortChange(context) {
      this.ordering = context.sortDesc ? '-' + context.sortBy : context.sortBy;
      this.fetchStudents();
    },

    onSearch() {
      // Reset to the first page when searching so the paginator and the
      // requested offset stay in sync.
      this.currentPage = 1;
      this.offset = 0;
      this.debouncedCourseSearch(this);
    },

    onProgramChange() {
      // Reset to the first page when the program-category filter changes.
      this.currentPage = 1;
      this.offset = 0;
      this.fetchAllCourses();
    },

    debouncedCourseSearch: debounce(vm => {
      vm.fetchAllCourses();
    }, 500),

    handleResize() {
      this.windowWidth = window.innerWidth;
    },
    openDuplicateModal(course) {
      this.selectedCourseId = course.id;
      this.duplicateForm.title = `${course.title} (Copy)`;
      this.duplicateForm.program_category = course.program_category?.id || null;
      this.showDuplicateModal = true;
    },
    resetDuplicateModal() {
      this.showDuplicateModal = false;
      this.selectedCourseId = null;
      this.duplicateForm = {
        title: '',
        program_category: null,
      };
      this.isDuplicateLoading = false;
    },
    async confirmDuplicateCourse(courseData) {
      const payload = courseData || this.duplicateForm;
      const title = payload?.title;
      const programCategory = payload?.program_category;
      if (!title || !programCategory) {
        this.makeToast({ variant: 'warning', msg: 'Please provide title and program category' });
        return;
      }
      // keep local state in sync with latest edits from modal
      this.duplicateForm = { title, program_category: programCategory };
      this.isDuplicateLoading = true;
      try {
        await this.duplicateCourse({
          id: this.selectedCourseId,
          data: {
            title,
            program_category: programCategory,
          },
        });
        this.makeToast({ variant: 'success', msg: 'Course duplicated successfully' });
        this.resetDuplicateModal();
        // Refetch from the first page so the new course appears in proper
        // sorted/paginated order instead of being prepended (which caused
        // duplicates and threw off the per-page count).
        this.currentPage = 1;
        this.offset = 0;
        await this.fetchAllCourses();
        this.isDuplicateLoading = false;
      } catch (error) {
        this.isDuplicateLoading = false;
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
  },

  async mounted() {
    await this.fetchPrograms();
    await this.fetchAllCourses();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>

<style scoped lang="scss">
.loader {
  text-align: center;
  color: #bbbbbb;
  margin-inline: auto;
}
</style>
